import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchClients } from '../store/clientsSlice';
import { setClientScopeMode, setSelectedClientIds, toggleSelectedClientId } from '../store/combinedReportsSlice';
import { buildCombinedClientOptions, resolveEffectiveClientIds } from './combinedReportUtils';

const areArraysEqual = (left, right) => left.length === right.length && left.every((item, index) => item === right[index]);

const buildSelectionPreview = (clientIds) => {
  if (clientIds.length === 0) {
    return 'No client IDs selected';
  }

  if (clientIds.length <= 3) {
    return clientIds.join(', ');
  }

  return `${clientIds.slice(0, 3).join(', ')} +${clientIds.length - 3} more`;
};

const useCombinedReportScope = () => {
  const dispatch = useDispatch();
  const { clientId: activeClientId, financialYear } = useSelector((state) => state.app);
  const profile = useSelector((state) => state.user.profile);
  const { clients, loading: clientsLoading } = useSelector((state) => state.clients);
  const { clientScopeMode, selectedClientIds } = useSelector((state) => state.combinedReports);

  const clientOptions = useMemo(() => buildCombinedClientOptions(profile, clients), [profile, clients]);
  const availableClientIds = useMemo(() => clientOptions.map((option) => option.value), [clientOptions]);

  const normalizedSelectedClientIds = useMemo(() => resolveEffectiveClientIds({
    clientScopeMode: 'selected',
    selectedClientIds,
    availableClientIds,
  }), [availableClientIds, selectedClientIds]);

  const effectiveClientIds = useMemo(() => resolveEffectiveClientIds({
    clientScopeMode,
    selectedClientIds: normalizedSelectedClientIds,
    availableClientIds,
  }), [availableClientIds, clientScopeMode, normalizedSelectedClientIds]);

  useEffect(() => {
    if (clientOptions.length === 0 && !clientsLoading) {
      dispatch(fetchClients());
    }
  }, [clientOptions.length, clientsLoading, dispatch]);

  useEffect(() => {
    if (!areArraysEqual(normalizedSelectedClientIds, selectedClientIds)) {
      dispatch(setSelectedClientIds(normalizedSelectedClientIds));
    }
  }, [dispatch, normalizedSelectedClientIds, selectedClientIds]);

  const handleScopeModeChange = (nextMode) => {
    if (nextMode === 'selected' && normalizedSelectedClientIds.length === 0) {
      const defaultClientId = availableClientIds.includes(activeClientId) ? activeClientId : availableClientIds[0];
      if (defaultClientId) {
        dispatch(setSelectedClientIds([defaultClientId]));
      }
    }

    dispatch(setClientScopeMode(nextMode));
  };

  const handleToggleClientId = (clientId) => {
    if (clientScopeMode !== 'selected') {
      dispatch(setClientScopeMode('selected'));
    }
    dispatch(toggleSelectedClientId(clientId));
  };

  return {
    financialYear,
    clientOptions,
    clientsLoading,
    clientScopeMode,
    selectedClientIds: normalizedSelectedClientIds,
    effectiveClientIds,
    scopeSummary: clientScopeMode === 'selected'
      ? `${effectiveClientIds.length} selected client${effectiveClientIds.length === 1 ? '' : 's'}`
      : `${availableClientIds.length} accessible client${availableClientIds.length === 1 ? '' : 's'}`,
    selectionPreview: buildSelectionPreview(effectiveClientIds),
    onScopeModeChange: handleScopeModeChange,
    onToggleClientId: handleToggleClientId,
  };
};

export default useCombinedReportScope;